package bm

import model.*
import org.w3c.dom.url.URL
import org.w3c.xhr.XMLHttpRequest
import react.*
import kotlinx.browser.window

interface AppProps : RProps {
    var interval: Int
    var restartingMessage: String
    var configFailedMessage: String
	var debugBackend: Boolean // set this to true if you're running the CI locally
	var debugFrontend: Boolean // set this to true if you're running the frontend locally, but using taktik.ci backend
	var password: String // set this if [debugFrontend] is true, but don't commit it !
    var pages: Array<String>
}

interface AppState : RState {
    var result: Result
    var restarting: Boolean
    var profile: String

    var pageToDisplay: String
    var projectToDisplay: String
    var branchToDisplay: String
    var releaseToDisplay: String
    var testRunsTooltip: TestRunsTooltip? // of the test badge under the mouse
}

class App(props: AppProps) : RComponent<AppProps, AppState>(props) {
    override fun AppState.init(props: AppProps) {
        result = Result()
        restarting = true
        profile = "default"

        pageToDisplay = "CI"
        projectToDisplay = ""
        branchToDisplay = ""
        releaseToDisplay = ""
        testRunsTooltip = null
    }

    private var timerID: Int? = null
    private var etag: String? = null // of the last /all received: while nothing changes, the backend answers 304 without a body

    override fun componentDidMount() {
        console.log("Starting...")
        timerID = window.setInterval({
            // actually, the operation is performed on a state's copy, so it stays effectively immutable
            fetchDataFromBackend()
            // The durations of the running builds are computed when rendering: rendered again every second, whatever /all
            // brings (a 304, i.e. nothing new, sets no state)
            setState { }
        }, props.interval)
    }

    override fun componentWillUnmount() {
        window.clearInterval(timerID!!)
    }

    override fun RBuilder.render() {
        renderer(this@App, state.result.projects, state.restarting, state.result.configFailed)
    }

    private fun fetchDataFromBackend() {
        val xhttp = XMLHttpRequest()
        xhttp.onreadystatechange = {
            if (xhttp.readyState == 4.toShort() && xhttp.status == 200.toShort()) {
                etag = xhttp.getResponseHeader("ETag")
                val newResult: Result = JSON.parse(xhttp.responseText)
                setState {
                    result = newResult
                    // from the result just received: a 304 brings no other to tell it from
                    restarting = !(newResult.projects.isNotEmpty() && newResult.projects.all { p -> p.done && p.branches.all { it.done } })
                    profile = when {
                        newResult.projects.any { it.name.lowercase() == "flowr" } -> Profiles.FLOWR
                        newResult.projects.any { it.name == "Tool-Taktik" } -> Profiles.ODOO
                        else -> Profiles.DEFAULT
                    }
                }
            } else if (xhttp.status == 403.toShort()) {
                val loginUrl = URL("/backend/saml/login", window.location.href)
                loginUrl.searchParams.append("target", window.location.href)
                window.location.href = loginUrl.toString()
            }
        }
        try {
			sendRequest(props, xhttp, "GET", "/all", etag)
        } catch (e: dynamic) {
            setState { restarting = true }
        }
    }
}

fun sendRequest(attrs: AppProps, xhttp: XMLHttpRequest, method: String, path: String, etag: String? = null) {
	val domain = if (attrs.debugBackend) "http://localhost:8080" else if (attrs.debugFrontend) "https://taktik.ci/backend" else "backend"
	xhttp.open(method, "$domain$path", true)
	if (attrs.debugBackend || attrs.debugFrontend) xhttp.setRequestHeader("Authorization", "Basic " + window.btoa("admin:${attrs.password}"))
	if (etag != null) xhttp.setRequestHeader("If-None-Match", etag)
	xhttp.send()
}

fun RBuilder.app(interval: Int = 1000) = child(App::class) {
    attrs.interval = interval
    attrs.restartingMessage = "Loading..."
    attrs.configFailedMessage = "Failed to parse last YAML config :("
	attrs.debugBackend = false
	attrs.debugFrontend = false
	attrs.password = ""
    attrs.pages = arrayOf("CI", "CD")
}
